import { SelectionModel } from '@angular/cdk/collections';
import {
  AfterViewInit,
  Component,
  OnInit,
  ViewChild,
  CUSTOM_ELEMENTS_SCHEMA,
  NO_ERRORS_SCHEMA,
  inject,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatAccordion } from '@angular/material/expansion';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { PaginationService } from '../../../helpers/pagination.service';
import { AccountingService } from '../accounting.service';
import { IAccount } from '../models/account.model';
import { invoiceDataGridColumns } from '../models/invoice-column-config';
import {
  IInvoiceRequestParams,
  IInvoiceResponseObject,
  Invoice,
  MutationEntry,
  MutationEntryLine,
} from '../models/invoice.model';
import { MatDateRangePicker } from '@angular/material/datepicker';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

@Component({
  selector: 'app-invoice',
  templateUrl: './invoice.component.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['../accounting-table.scss', './invoice.component.scss'],
})
export class InvoiceComponent implements OnInit, AfterViewInit {
  @ViewChild(MatAccordion) accordion!: MatAccordion;

  visibleFieldKeys: string[] = [
    'select',
    'invoiceDate',
    'importDate',
    'role',
    'taskId',
    'invoiceId',
    'profileName',
    'amount',
    'importStatus',
    'actions',
  ];
  data: any[] = [];
  selection = new SelectionModel<any>(true, []);
  pageLimit = 1000;
  pageSize: number = 20;
  pageOffset: number = 0; // Default page offset
  pageIndex: number = 0;
  columns = invoiceDataGridColumns;
  displayedColumns: string[] = [];
  dataSource = new MatTableDataSource<any>([]);

  invoiceForm!: FormGroup;

  columnName: string = 'Id';
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  sortedData: Invoice[] = [];
  totalRecords: number = 0;
  @ViewChild('bookingPicker') bookingPicker!: MatDateRangePicker<Date>;
  minDate!: Date;
  maxDate!: Date;
  startDate!: Date;
  endDate!: Date;
  sortOrder: string = 'desc';
  searchQuery: string = '';

  constructor(
    private _accountingService: AccountingService,
    private cdr: ChangeDetectorRef,
    private paginationService: PaginationService,
    private _snackBar: MatSnackBar,
    private router: Router,
    private fb: FormBuilder,
  ) {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();
    const currentDate = new Date().getDate();
    this.minDate = new Date(2022, 9, 93);
    this.maxDate = new Date(currentYear, currentMonth, currentDate);
  }

  openDatePicker(): void {
    this.bookingPicker.open(); // Opens the date picker when the image is clicked
  }

  ngOnInit(): void {
    this.initializeDefaultBookingDates();
    this.createInvoiceForm();
    this.invoiceData(
      this.startDate,
      this.endDate,
      this.columnName,
      this.sortOrder,
      this.pageOffset,
      this.pageSize,
      this.searchQuery,
    );
  }

  createInvoiceForm() {
    this.invoiceForm = this.fb.group({
      startDate: [this.startDate, Validators.required],
      endDate: [this.endDate, Validators.required],
    });
  }

  initializeDefaultBookingDates() {
    this.startDate = new Date(new Date().getFullYear(), 0, 1); // Start: Jan 1st of current year
    // Start: Jan 1st of current year
    this.endDate = new Date(); // End: today
  }

  invoiceData(
    startDate: Date,
    endDate: Date,
    columnName: string,
    sortOrder: string,
    pageOffset: number,
    pageSize: number,
    searchQuery: string,
  ) {
    const params: IInvoiceRequestParams = {
      startDate: startDate,
      endDate: endDate,
      columnName: columnName,
      sortOrder: sortOrder,
      pageOffset: this.pageOffset,
      pageSize: this.pageSize,
      searchQuery: searchQuery,
    };
    this._accountingService.getInvoiceData(params).subscribe({
      next: (invoices: IInvoiceResponseObject) => {
        const invoiceData = invoices?.value.financeMutationRows;
        this.totalRecords = invoices?.value.finMutationCount;
        invoiceData.forEach((element: Invoice) => {
          element.strImportDate === '' ? '-' : element.strImportDate;
          element.creationDate = element.creationDate
            ? element.creationDate.split(',')[0]
            : '-';
          element.invoiceDate = element.invoiceDate
            ? element.invoiceDate.split(',')[0]
            : 'No Date';
          element.importDate = element.importDate
            ? element.importDate.split(',')[0]
            : 'No Date';
        });
        this.dataSource.data = [...this.dataSource.data, ...invoiceData];
        this.sortedData = this.dataSource.data.slice(this.pageOffset);
        this.cdr.detectChanges();
      },
      error: () => {
        this._snackBar.open('Failed to fetch invoices data', 'Close', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        });
      },
    });
  }

  resetData() {
    this.dataSource.data = [];
    this.pageOffset = 0;
    this.totalRecords = 0;
  }

  onDateRangeChange() {
    const startDate = this.invoiceForm.controls['startDate'].value;
    const endDate = this.invoiceForm.controls['endDate'].value;
    this.resetData();
    if (!startDate || !endDate) return;
    // this.currentOffset = 0;
    // // this.isFetching = false;
    if (
      (this.maxDate !== null && this.minDate !== null) ||
      this.maxDate > this.minDate
      // this.companyId !== null &&
      // this.selectedCategoryIds !== null)
    ) {
      if (
        this.minDate.getTime() != startDate &&
        this.maxDate.getTime() != endDate
      ) {
        this.startDate = startDate;
        this.endDate = endDate;
        this.invoiceData(
          startDate,
          endDate,
          this.columnName,
          this.sortOrder,
          this.pageIndex,
          this.pageSize,
          this.searchQuery,
        );
      } else {
        this.startDate = this.minDate;
        this.endDate = this.maxDate;
        this.invoiceData(
          this.startDate,
          this.endDate,
          this.columnName,
          this.sortOrder,
          this.pageIndex + 1,
          this.pageSize,
          this.searchQuery,
        );
      }
    }
  }

  handleSortChange(event: Sort) {
    this.dataSource.data = [];
    if (event.active && event.direction) {
      this.invoiceData(
        this.startDate,
        this.endDate,
        event.active,
        event.direction,
        this.pageIndex,
        this.pageOffset,
        this.searchQuery,
      );
    } else {
      //TODO remove after testing
      // this._snackBar.open("Sorting is been cleared", "Close", {
      //   duration: 3000,
      //   horizontalPosition: "center",
      //   verticalPosition: "top",
      // });
    }
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    const startIndex = this.pageIndex * this.pageOffset;
    const endIndex = startIndex + this.pageOffset;
    // this.paginator.length = this.dataSource.data.length;

    this.sortedData = this.dataSource.data.slice(startIndex, endIndex);
    // this.sortCurrentPageData(); // Apply sorting after fetching data
    this.updateDisplayedColumns();

    this.sort.sortChange.subscribe(() => {
      this.sortCurrentPageData();
    });
  }

  updateDisplayedColumns() {
    this.displayedColumns = this.visibleFieldKeys;
  }

  toggleColumnVisibility(key: string) {
    if (this.visibleFieldKeys.includes(key)) {
      // If the key is already visible, remove it.
      this.visibleFieldKeys = this.visibleFieldKeys.filter((k) => k !== key);
    } else {
      // Find the original index of the key in the full columns array.
      const originalIndex = this.columns.findIndex((col) => col.key === key);

      // Find the correct insertion point in the visibleFieldKeys array.
      let insertIndex = this.visibleFieldKeys.length; // Default to end

      for (let i = 0; i < this.visibleFieldKeys.length; i++) {
        const visibleKey = this.visibleFieldKeys[i];
        // Find the original index of the currently visible key
        const visibleOriginalIndex = this.columns.findIndex(
          (col) => col.key === visibleKey,
        );

        // If the new key's original index is less than the current visible key's original index,
        // it means we've found our insertion point.
        if (originalIndex < visibleOriginalIndex) {
          insertIndex = i;
          break; // Stop searching once the position is found
        }
      }

      // Insert the new key at the determined index.
      this.visibleFieldKeys.splice(insertIndex, 0, key);
    }

    // Update the displayed columns to reflect the change.
    this.updateDisplayedColumns();
  }

  sortCurrentPageData() {
    if (this.sort.active && this.sort.direction !== '') {
      const startIndex = this.pageIndex; //* this.currentPageSize;
      const endIndex = startIndex + this.pageSize;
      this.paginator.length = this.dataSource.data.length;

      const currentPageData = this.dataSource.data.slice(startIndex, endIndex);
      // this.sortedData = currentPageData.sort((a, b) => {
      //   const isAsc = this.sort.direction === 'asc';
      //   const isDesc = this.sort.direction === 'desc';
      //   switch (this.sort.active) {
      //     case 'id':
      //       return this.compare(a.id, b.id, isAsc);
      //     case 'taskId':
      //       return this.compare(a.taskId, b.taskId, isAsc);
      //     case 'importStatus':
      //       return this.compare(a.importStatus, b.importStatus, isAsc);
      //     case 'profileId':
      //       return this.compare(a.profileId, b.profileId, isAsc);
      //     case 'type':
      //       return this.compare(a.type, b.type, isAsc);
      //     case 'invoiceId':
      //       return this.compare(a.invoiceId, b.invoiceId, isAsc);
      //     case 'amount':
      //       return this.compare(a.amount, b.amount, isAsc);
      //     case 'amountDownpayment':
      //       return this.compare(
      //         a.amountDownpayment,
      //         b.amountDownpayment,
      //         isAsc,
      //       );
      //     case 'amountAdminFee':
      //       return this.compare(a.amountAdminFee, b.amountAdminFee, isAsc);
      //     case 'amountVat':
      //       return this.compare(a.amountVat, b.amountVat, isAsc);
      //     case 'eolInvoiceNumber':
      //       return this.compare(a.eolInvoiceNumber, b.eolInvoiceNumber, isAsc);
      //     case 'eolContactId':
      //       return this.compare(a.eolContactId, b.eolContactId, isAsc);
      //     case 'invoiceDate':
      //       return this.sortingDate(
      //         this.parseDate(a.invoiceDate),
      //         this.parseDate(b.invoiceDate),
      //         isAsc,
      //       );
      //     case 'creationDate':
      //       return this.sortingDate(
      //         this.parseDate(a.creationDate),
      //         this.parseDate(b.creationDate),
      //         isAsc,
      //       );
      //     case 'importDate':
      //       return this.sortingDate(
      //         this.parseDate(a.importDate),
      //         this.parseDate(b.importDate),
      //         isDesc,
      //       );
      //     case 'importCounter':
      //       return this.compare(a.importCounter, b.importCounter, isAsc);
      //     case 'journal':
      //       return this.compare(a.journal, b.journal, isAsc);

      //     default:
      //       return 0;
      //   }
      // });
    }
  }

  // Custom comparison function
  compare(a: number | string, b: number | string, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  sortingDate(
    a: number | string | Date,
    b: number | string | Date,
    isAsc: boolean,
  ) {
    // Convert string dates to Date objects if they are not already
    const dateA = new Date(a);
    const dateB = new Date(b);

    // Compare the dates
    return (dateA.getTime() - dateB.getTime()) * (isAsc ? 1 : -1);
  }

  parseDate(dateString: string): Date {
    const [day, month, year] = dateString.split('-').map(Number); // Split the string and convert to numbers
    return new Date(year, month - 1, day); // Create a Date object (month is zero-based)
  }

  onPageChange($event: PageEvent) {
    this.pageIndex = $event.pageIndex;
    this.pageSize = $event.pageSize;
    this.pageOffset = $event.pageIndex * this.pageSize; // Calculate page offset for API calls
    // this.dataSource.data = []
    // this.sortedData = this.sortedData.slice(startIndex, endIndex);
    const startIndex = this.pageIndex * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.sortedData = this.dataSource.data.slice(startIndex, endIndex);
    this.invoiceData(
      this.startDate,
      this.endDate,
      this.columnName,
      this.sortOrder,
      this.pageOffset,
      this.pageSize,
      this.searchQuery,
    );
    this.cdr.detectChanges();

    const offsetReached = ($event.pageIndex + 1) * $event.pageSize;
    const isNearEnd = offsetReached >= this.dataSource.data.length;
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  /** Selectsall rows if they are not all selected; otherwise clear selection. */
  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.selection.select(...this.dataSource.data);
  }

  checkboxLabel(row?: any): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.position + 1}`;
  }

  searchInvoiceQuery(event: any) {
    const searchValue = event.target.value.toLowerCase();
    this.dataSource.filter = searchValue.trim().toLowerCase();
    this.sortedData = this.dataSource.filteredData.slice(0, this.pageSize);
  }

  saveInvoiceDetails(row: Invoice) {
    this._accountingService
      .saveEolContactId(row.id, row.eolContactId)
      .subscribe({
        next: (res: IAccount) => {
          this._snackBar.open('Saved successfully', 'Close', {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
          });

          const index = this.dataSource.data.findIndex(
            (item) => item.profileId === res.profileId,
          );
          if (index !== -1) {
            this.dataSource.data[index] = res;
          }
        },
        error: (error) => {
          this._snackBar.open(
            `${error}` + 'Failed to save invoice details',
            'Close',
            {
              duration: 3000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
            },
          );
        },
      });
  }

  deleteInvoice(row: Invoice) {
    this._accountingService.deleteContact(row.profileId).subscribe({
      next: (res: number) => {
        this._snackBar.open('Invoice deleted successfully', 'Close', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        });
        this.dataSource.data = this.dataSource.data.filter(
          (item) => item.profileId !== row.profileId,
        );
        this.sortedData = this.dataSource.data.slice(0, this.pageSize);
      },
      error: (error) => {
        this._snackBar.open('Failed to delete account', 'Close', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        });
      },
    });
  }

  // Logic to show invoice logs
  showMutationEntriesByFinanceMutationId(row: MutationEntry) {
    this._accountingService
      .getMutationEntriesByFinanceMutationId(row.id)
      .subscribe({
        next: (res: Invoice[]) => {
          this._snackBar.open('Entry logs fetched successfully', 'Close', {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
          });
          this.showMutationEntryLinesByFinanceMutationId(row);
        },
        error: (error) => {
          this._snackBar.open('Failed to fetch entry logs', 'Close', {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
          });
        },
      });
  }

  showMutationEntryLinesByFinanceMutationId(row: MutationEntry) {
    this._accountingService
      .getFinanceEntryLinesByFinanceMutationId(row.financeMutationId)
      .subscribe({
        next: (res: MutationEntryLine[]) => {
          this._snackBar.open(
            'Invoice entry lines fetched successfully',
            'Close',
            {
              duration: 3000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
            },
          );
        },
        error: (error) => {
          this._snackBar.open('Failed to fetch invoice entry lines', 'Close', {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
          });
        },
      });
  }

  importData(ids: any) {
    const selectedIds = ids?.selected.map((row: any) => row.id);
    this._accountingService.importInvoiceData(selectedIds).subscribe({
      next: (res: number[]) => {
        this._snackBar.open('Import successful', 'Close', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        });
        // Optionally, refresh the data or update the specific rows in the dataSource
        // this.dataSource.data = this.dataSource.data.filter(item => !selectedIds.includes(item.id));
        // this.sortedData = this.dataSource.data.slice(0, this.currentPageSize);
        this.selection.clear(); // Clear selection after import
      },
      error: (error) => {
        this._snackBar.open('Failed to import invoices', 'Close', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        });
      },
    });
  }

  /**
   * TODO: Implement export functionality.
   * This method is a placeholder for exporting data.
   */
  exportData() {
    // Logic to export data
    this._snackBar.open(
      'Export functionality is not implemented yet',
      'Close',
      {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'top',
      },
    );
  }

  openInvoiceDetails(rowData: Invoice) {
    const invoiceId = rowData.id;
    // sessionStorage.setItem('currentNavName', invoiceId);
    sessionStorage.setItem('invoiceData', JSON.stringify(rowData));
    this.router.navigate([
      '/klusup/accounting/invoice/invoice-details',
      invoiceId,
    ]);
  }
}
